import * as z from 'zod'
import type {
  ProducttypeFunnel,
  ProducttypeFunnelPage,
  ProducttypeFunnelPotential,
  ProducttypeFunnelStatusCounts,
  ProducttypeStatistics,
  ProducttypeStatisticsCategoryEntry,
  ProducttypeStatisticsMarginGroupEntry,
  ProducttypeStatisticsMonthlyTrendEntry,
  ProducttypeStatisticsTopTopic,
  Topic,
  TopicDecision,
  TopicPage,
} from '@/api/topics/types'

const topicStatusSchema = z.enum(['Open', 'Approved', 'Ignored'])
const topicCategorySchema = z.enum(['Recipient', 'Motif', 'Hobby', 'Trait', 'Material', 'Occasion'])

const keywordSummarySchema = z.object({
  keywordId: z.number(),
  value: z.string(),
  subtype: z.string().nullable(),
  searchVolumePerMonthAvg: z.number().nullable(),
})

const monthlyVolumeSchema = z.object({
  year: z.number(),
  month: z.number(),
  searchVolume: z.number(),
})

const marginGroupSummarySchema = z.object({
  key: z.string(),
  label: z.string(),
})

const linkedNicheSchema = z.object({
  id: z.number(),
  name: z.string(),
})

const topicRankingSchema = z.object({
  score: z.number(),
  position: z.number(),
  searchVolume: z.number(),
  marginPerUnit: z.number(),
  rankedAt: z.string().nullable(),
})

/**
 * Laufzeitvertrag der Themen-API.
 *
 * Die Typangabe stellt beim Kompilieren sicher, dass Schema und die in
 * der benachbarten types.ts dokumentierten DTOs nicht unabhängig
 * voneinander driften.
 */
export const topicSchema: z.ZodType<Topic> = z.object({
  id: z.number(),
  topic: z.string(),
  category: topicCategorySchema,
  categoryLabel: z.string(),
  status: topicStatusSchema,
  statusChangedAt: z.string().nullable(),
  statusChangedBy: z.string().nullable(),
  niche: linkedNicheSchema.nullable(),
  producttypeKey: z.string(),
  producttypeName: z.string().nullable(),
  marginGroup: marginGroupSummarySchema.nullable(),
  keywords: z.array(keywordSummarySchema),
  keywordCount: z.number(),
  searchVolumeMax: z.number().nullable(),
  searchVolumeSum: z.number().nullable(),
  cpc: z.number().nullable(),
  cmp: z.number().nullable(),
  trend: z.number().nullable(),
  ranking: topicRankingSchema.nullable(),
  monthlyVolumes: z.array(monthlyVolumeSchema).nullable(),
})

export const topicPageSchema: z.ZodType<TopicPage> = z.object({
  items: z.array(topicSchema),
  totalCount: z.number(),
  page: z.number(),
  pageSize: z.number(),
})

export const topicDecisionSchema: z.ZodType<TopicDecision> = z.object({
  topicId: z.number(),
  status: topicStatusSchema,
  nicheId: z.number().nullable(),
  nicheName: z.string().nullable(),
  nicheCreated: z.boolean(),
  nicheOrphaned: z.boolean(),
})

const producttypeFunnelStatusCountsSchema: z.ZodType<ProducttypeFunnelStatusCounts> = z.object({
  total: z.number(),
  open: z.number(),
  approved: z.number(),
  ignored: z.number(),
})

const producttypeFunnelPotentialSchema: z.ZodType<ProducttypeFunnelPotential> = z.object({
  total: z.number(),
  open: z.number(),
  approved: z.number(),
  ignored: z.number(),
})

export const producttypeFunnelSchema: z.ZodType<ProducttypeFunnel> = z.object({
  producttypeKey: z.string(),
  producttypeName: z.string(),
  marginGroup: marginGroupSummarySchema.nullable(),
  topicCounts: producttypeFunnelStatusCountsSchema,
  potential: producttypeFunnelPotentialSchema,
  topicsWithoutPotential: z.number(),
})

export const producttypeFunnelPageSchema: z.ZodType<ProducttypeFunnelPage> = z.object({
  items: z.array(producttypeFunnelSchema),
  totalCount: z.number(),
  page: z.number(),
  pageSize: z.number(),
  calculatedAt: z.string(),
  isComplete: z.boolean(),
  calculationRule: z.string(),
})

const producttypeStatisticsCategoryEntrySchema: z.ZodType<ProducttypeStatisticsCategoryEntry> =
  z.object({
    category: topicCategorySchema,
    categoryLabel: z.string(),
    count: z.number(),
    knownPotentialSum: z.number(),
  })

const producttypeStatisticsMarginGroupEntrySchema: z.ZodType<ProducttypeStatisticsMarginGroupEntry> =
  z.object({
    marginGroupKey: z.string().nullable(),
    marginGroupLabel: z.string().nullable(),
    count: z.number(),
    knownPotentialSum: z.number(),
  })

const producttypeStatisticsTopTopicSchema: z.ZodType<ProducttypeStatisticsTopTopic> = z.object({
  id: z.number(),
  topic: z.string(),
  producttypeKey: z.string(),
  producttypeName: z.string().nullable(),
  value: z.number(),
})

const producttypeStatisticsMonthlyTrendEntrySchema: z.ZodType<ProducttypeStatisticsMonthlyTrendEntry> =
  z.object({
    year: z.number(),
    month: z.number(),
    searchVolume: z.number(),
  })

export const producttypeStatisticsSchema: z.ZodType<ProducttypeStatistics> = z.object({
  totalCount: z.number(),
  statusCounts: producttypeFunnelStatusCountsSchema,
  knownPotentialSum: z.number(),
  countWithoutPotential: z.number(),
  rankedCount: z.number(),
  categoryDistribution: z.array(producttypeStatisticsCategoryEntrySchema),
  marginGroupDistribution: z.array(producttypeStatisticsMarginGroupEntrySchema),
  topByPotential: z.array(producttypeStatisticsTopTopicSchema),
  topByRankingScore: z.array(producttypeStatisticsTopTopicSchema),
  monthlyTrend: z.array(producttypeStatisticsMonthlyTrendEntrySchema),
  calculatedAt: z.string(),
  isComplete: z.boolean(),
})
